import { useTracking } from 'react-tracking';
import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import CommentOutline from '@simplywallst/ui-core/icons/CommentOutline';
import UserProfileOutline from '@simplywallst/ui-core/icons/UserProfileOutline';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { NarrativeAuthor } from '@/components/NarrativeAuthor';
import { Badge, Tooltip } from '@simplywallst/ui-core/next';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';

import {
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_NARRATIVE_COMMUNITY_DETAIL,
} from '@/constants/routes';
import { useFastCommentsCount } from '@components/FastCommentWidget';

import type { FragmentOf } from 'gql.tada';
import { readFragment } from 'gql.tada';
import { NARRATIVE_CARD_FRAGMENT } from './narrativeCardFragment';
import { NarrativeCardDropdown } from './components/NarrativeCardDropdown';
import { Heading, cn } from '@simplywallst/ui-core/next';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import {
  getCompanyUrlSegments,
  getNarrativeUrl,
} from '@/pages/Narrative/utils/getNarrativeUrl';
import { NarrativeCardExcerpt } from './components/NarrativeCardExcerpt';
import { NarrativeCardActions } from './components/NarrativeCardActions';
import { usePathname } from '@/hooks/location/usePathname';
import { RouterLinkOptimised } from '../RouterLinkOptimised';
import type { HeadingElements } from '@/utilities/heading';
import { NarrativeCardTimeBadge } from './components/NarrativeCardTimeBadge';
import { useUserProfile } from '@/hooks/useUserProfile';
import { ValuationMetrics } from '@/pages/Narrative/components/ValuationMetrics';
import { CompanyLogo } from '../CompanyLogo';

export type PopulatedStateBaseProps = {
  onClick?: () => void;
  onSetFairValueError?: () => void;
  className?: string;
  showCompanyIcon?: boolean;
  /**
   * The path to return to after editing a narrative.
   * Defaults to the current pathname
   */
  returnPath?: string;
  titleAs?: HeadingElements;
};

type Props = PopulatedStateBaseProps & {
  narrative: FragmentOf<typeof NARRATIVE_CARD_FRAGMENT>;
};

export const PopulatedState = ({
  narrative,
  onClick,
  onSetFairValueError,
  className,
  showCompanyIcon,
  returnPath,
  titleAs = 'h3',
}: Props) => {
  const { trackEvent } = useTracking();
  const {
    company,
    primaryCount,
    slug,
    updatedAt,
    owner,
    valuation,
    voteCount,
    isPublic,
    commentKey,
    type,
    isSponsored,
    draftUpdate,
    earliestPublishedUpdate,
    latestPublishedUpdate,
    excerpt,
    title,
  } = readFragment(NARRATIVE_CARD_FRAGMENT, narrative);
  const { commentCount } = useFastCommentsCount(commentKey);
  const pathname = usePathname();

  const { analysisValue, data, name, info, tickerSymbol } = company ?? {};
  const { publicId: currentUserPublicId } = useUserProfile();
  const isOwner = currentUserPublicId === owner.publicId;
  const currencyIso = getTradingItemCurrencyISO(data?.currencyInfo);
  const isDraft = isOwner && !earliestPublishedUpdate?.willLockAt;

  const targetExcerpt =
    draftUpdate?.coreExcerpt || latestPublishedUpdate?.coreExcerpt || excerpt; // TODO post migration, remove excerpt
  const targetTitle =
    draftUpdate?.title || latestPublishedUpdate?.title || title;
  const targetIntrinsicDiscount =
    draftUpdate?.valuation?.intrinsicDiscount || valuation?.intrinsicDiscount;
  const targetFairValue =
    draftUpdate?.valuation?.fairValue || valuation?.fairValue;
  const targetRevenuePercentageChange =
    draftUpdate?.valuation?.revenuePercentageChange ||
    valuation?.revenuePercentageChange;

  const formattedFairValue = toCurrencyTruncated(
    targetFairValue || 0,
    2,
    currencyIso ?? 'XXX'
  );

  const titleNode = (
    <Heading
      as={titleAs}
      className={cn(
        'm-0 line-clamp-2 text-base font-strong text-solid',
        isPublic && 'hover:underline focus:underline'
      )}
    >
      {targetTitle || `I believe ${name}'s Fair Value is ${formattedFairValue}`}
    </Heading>
  );

  const { canonicalURL } = company ?? { canonicalURL: '' };

  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(canonicalURL);

  const isViewingCompanyReport = pathname.includes('/stocks/');
  const isCompanyPeerNarrative =
    getCompanyUrlSegments(pathname)?.companyName !== companyName &&
    getCompanyUrlSegments(pathname)?.industry === industry;
  const shouldHideNarrativeActions =
    isViewingCompanyReport && isCompanyPeerNarrative && isSponsored;

  const dropdown = (
    <NarrativeCardDropdown
      publicId={narrative?.owner?.publicId}
      narrativeId={narrative.id}
      canonicalUrl={getNarrativeUrl({
        canonicalURL,
        narrativeSlug: slug,
      })}
      valuationType="USER_DEFINED"
    />
  );

  return (
    <div className={cn('overflow-hidden rounded-x1_5 bg-surface-2', className)}>
      <div
        className={cn(
          'relative grid gap-4 bg-surface-3 p-x2 transition duration-300 ease-out',
          showCompanyIcon ? 'h-[230px]' : 'h-[189.5px]',
          isPublic && 'hover:bg-p-grey-10 focus:bg-p-grey-10'
        )}
      >
        {showCompanyIcon && (
          <div
            className={cn(
              'grid min-h-x3 grid-cols-[1fr_max-content] items-center justify-start gap-x1',
              Boolean(isSponsored && dropdown) &&
                'grid-cols-[minmax(0,1fr)_max-content_max-content]'
            )}
          >
            <RouterLinkOptimised
              className="focus-outline grid grid-cols-[auto] no-underline decoration-solid hover:underline"
              to={{
                type: ROUTE_NARRATIVE_COMMUNITY,
                payload: {
                  country,
                  exchange_ticker: exchangeTicker,
                  industry,
                  companyname: companyName,
                  slug,
                },
              }}
              onClick={() =>
                trackEvent({
                  action: 'click',
                  modifier: 'company',
                  companyId: company?.id,
                  uniqueSymbol: company?.tickerSymbol,
                })
              }
            >
              <div className="grid grid-cols-[max-content_auto] items-center gap-x1">
                {tickerSymbol && (
                  <CompanyLogo
                    website={info?.url}
                    tickerSymbol={tickerSymbol}
                    size={20}
                    hasLogo={!!info?.hasLogo}
                  />
                )}
                <span className="truncate text-tiny text-soft">{name}</span>
              </div>
            </RouterLinkOptimised>
            {isSponsored && (
              <Tooltip
                placement="top"
                content={
                  <p className="m-0 w-x20 text-tiny text-solid">
                    {`This Narrative is sponsored by ${company?.name}. Simply Wall St received a fee to promote it. The views are those of the Sponsor and do not reflect Simply Wall St's opinions.`}
                  </p>
                }
              >
                <button>
                  <Badge className="grid grid-cols-[min-content_max-content] items-center justify-start gap-x0_25 bg-[--s-button-secondary-color-bg]">
                    <InformationSolid className="size-4 fill-chart-01" />
                    <span className="text-chart-01">Sponsored</span>
                  </Badge>
                </button>
              </Tooltip>
            )}
            {dropdown}
          </div>
        )}
        <div
          className={cn(
            'grid items-center gap-x1',
            Boolean(isDraft && !showCompanyIcon && dropdown)
              ? 'grid-cols-[1fr_max-content_max-content]'
              : 'grid-cols-[1fr_max-content]'
          )}
        >
          <NarrativeAuthor
            owner={{
              displayName: owner.displayName,
              publicId: owner.publicId,
              imageUrl: owner.imageUrl,
              role: owner.role,
              disclaimerType: owner.disclaimerType,
            }}
            priceTargetAnalystCount={analysisValue?.priceTargetAnalystCount}
            isAiNarrative={type === 'AI'}
          />
          {isDraft && (
            <div className="grid justify-end">
              <Badge tone="warning">Draft</Badge>
            </div>
          )}
          {!showCompanyIcon && dropdown}
        </div>
        {isPublic ? (
          <RouterLinkOptimised
            className="min-h-x13 no-underline decoration-p-white"
            to={{
              type: ROUTE_NARRATIVE_COMMUNITY_DETAIL,
              payload: {
                country,
                exchange_ticker: exchangeTicker,
                industry,
                companyname: companyName,
                slug,
              },
            }}
            onClick={onClick}
          >
            {titleNode}
            <NarrativeCardExcerpt canView>{targetExcerpt}</NarrativeCardExcerpt>
          </RouterLinkOptimised>
        ) : (
          <div className="min-h-x13">
            <div className="cursor-default">{titleNode}</div>
            <NarrativeCardExcerpt>{targetExcerpt}</NarrativeCardExcerpt>
          </div>
        )}
      </div>
      <div className="grid h-44 gap-4 p-4">
        <ValuationMetrics
          formattedFairValue={formattedFairValue}
          targetIntrinsicDiscount={targetIntrinsicDiscount}
          targetRevenuePercentageChange={targetRevenuePercentageChange}
        />
        {!shouldHideNarrativeActions && company?.id && (
          <NarrativeCardActions
            narrative={narrative}
            returnPath={returnPath || pathname}
            onError={onSetFairValueError}
          />
        )}
        {shouldHideNarrativeActions && <div className="h-x5"></div>}
        <div className="grid grid-cols-2 justify-between gap-x2">
          <div className="grid grid-cols-[repeat(3,max-content)] justify-start gap-x2 fill-softer text-xs text-softer">
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <LikeOutline className="size-x2" />

              <div>
                {voteCount.upvotes}
                <span className="sr-only">users have liked this narrative</span>
              </div>
            </div>
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <CommentOutline className="size-x2" />

              <div>
                {commentCount}
                <span className="sr-only">
                  users have commented on this narrative
                </span>
              </div>
            </div>
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <UserProfileOutline className="size-x2" />

              <div>
                {primaryCount}
                <span className="sr-only">
                  users have followed this narrative
                </span>
              </div>
            </div>
          </div>
          <NarrativeCardTimeBadge
            narrativeWillLockAt={earliestPublishedUpdate?.willLockAt}
            updatedAt={draftUpdate?.updatedAt ?? updatedAt}
            updateWillLockAt={latestPublishedUpdate?.willLockAt}
          />
        </div>
      </div>
    </div>
  );
};
